import React from 'react';
import { IntlProvider } from 'react-intl';
import { ColorScheme, ColorSchemeProvider, MantineProvider } from '@mantine/core';
import { useHotkeys, useLocalStorage, useFavicon } from '@mantine/hooks';
import { NotificationsProvider } from '@mantine/notifications';
import dayjs from 'dayjs';
import appLocales from '../../lang';
import { AuthProvider } from '../AuthProvider';
import { GlobalModalProvider } from '../../libs/ui-atoms/providers/GlobalModalProvider';
import { AppSettingsProvider } from '../AppSettingsProvider';
import { flattenMessages } from '../../libs/i18n';
import { appThemeColors, appThemeOther } from '../AppThemeProvider';
import { AppStyleProvider } from '../AppStyleProvider';

const AppProviders = ({ children }: { children: React.ReactNode }) => {
  useFavicon('/favicon.ico');

  const currentLanguage = 'fr';
  const { locale, messages } = appLocales[currentLanguage];

  dayjs.locale(currentLanguage);

  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: 'antibio-color-scheme',
    defaultValue: 'light',
  });

  const toggleColorScheme = (color?: ColorScheme) => {
    setColorScheme(color || (colorScheme === 'dark' ? 'light' : 'dark'));
  };

  useHotkeys([['mod+J', () => toggleColorScheme()]]);

  return (
    <AppSettingsProvider defaultSettings={{ currentLanguage }}>
      <IntlProvider locale={locale} messages={flattenMessages(messages)}>
        <NotificationsProvider position="top-center" zIndex={999999999999}>
          <ColorSchemeProvider colorScheme={colorScheme} toggleColorScheme={toggleColorScheme}>
            <MantineProvider
              theme={{
                colorScheme,
                fontFamily: 'Segoe UI, Roboto, sans-serif',
                dateFormat: 'YYYY-MM-DD',
                datesLocale: locale,
                loader: 'dots',
                colors: { ...appThemeColors },
                other: { ...appThemeOther },
              }}
            >
              <AppStyleProvider>
                <AuthProvider>
                  <GlobalModalProvider>{children}</GlobalModalProvider>
                </AuthProvider>
              </AppStyleProvider>
            </MantineProvider>
          </ColorSchemeProvider>
        </NotificationsProvider>
      </IntlProvider>
    </AppSettingsProvider>
  );
};

export default AppProviders;
